import { useEffect, useState } from 'react';

import { FacilityFieldsFragment, BffBrandType, BffFacility, NearbyFacility } from '@/graphql/__generated/sdk';

import { brandThemeToFacilityBrand } from './brandThemeToFacilityBrand';
import { getBffFacilityById } from './getBffFacilityById';
import { getNearbyFacilitiesFromLocation } from './getNearbyFacilitiesFromLocation';
import { getOnPageSchedulingDetails, IOnPageSchedulingDetails } from './getOnPageSchedulingDetails';
import { getPosition } from './getPosition';
import { useFacilityCode } from './useFacilityCode';

import { IUser } from '@shared-components/constants';
import { IAppContext, useAppContext } from '@shared-components/context/AppContextProvider.ctx';
import { Maybe, TAGBrand } from '@shared-components/types';
import { mapFacilities } from '@utils/mapFacilitiesResults';
import { preventMultipleCalls } from '@utils/preventMultipleCalls';

export type FacilityContextType = {
	officeInfo: FacilityFieldsFragment | null;
	facilityCode?: string;
	locationState: {
		nearByOfficeLocations: Array<NearbyFacility> | null;
		closestOfficeInfo: NearbyFacility | null;
		distance?: number | null;
		schedulingSystem?: string | null;
		user: IUser;
	};
	facilityBrand: BffBrandType;
	nearbyFacilities: BffFacility[] | undefined | null;
	isWaitingForNearbyFacilities: boolean;
};

export type SSRFacilityContextType = Omit<FacilityContextType, 'isWaitingForNearbyFacilities' | 'facilityBrand'>;

export interface UseFacilitiesSSRParams {
	brand;
	user;
	facilityCode;
	defaultToCdn;
	shouldFetchNearbyFacility;
	ssrData;
}

export function makeFacilitiesLoadingConfig(
	appConfig: Pick<IAppContext, 'config' | 'environment' | 'appName' | 'appVersion'>,
	brand: TAGBrand
): FacilitiesLoadingConfig {
	const { config, environment, appName, appVersion } = appConfig;
	const bffGraphqlUrl =
		environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
	const useFacilityBeffeDataSource = config.featureFlags.useFacilityBeffeDataSource;
	const transitionallyUseBeffe = config.featureFlags.transitionallyUseBeffe;
	const useBeffeForFacility = useFacilityBeffeDataSource || transitionallyUseBeffe;
	return {
		appName,
		appVersion,
		bffGraphqlUrl,
		brand,
		useBeffeForFacility,
		useFacilityBeffeDataSource,
		user: null,
	};
}

export function makeDefaultFacilityContextData(): SSRFacilityContextType {
	return {
		officeInfo: null,
		facilityCode: '',
		locationState: {
			nearByOfficeLocations: null,
			closestOfficeInfo: null,
			distance: null,
			user: {
				hasIpBasedCoordinates: false,
				coordinates: {
					latitude: 0,
					longitude: 0,
				},
			},
		},
		nearbyFacilities: [],
	};
}

export default function useFacilities({
	brand,
	user,
	facilityCode,
	defaultToCdn,
	shouldFetchNearbyFacility,
	ssrData,
}: UseFacilitiesSSRParams): FacilityContextType {
	const { facilityCode: tempFacilityCode, setFacilityCode, removeFacilityCode } = useFacilityCode(facilityCode);
	const [facilityContextData, setFacilityContextData] = useState(() => ssrData ?? makeDefaultFacilityContextData());
	const [isWaitingForNearbyFacilities, setIsWaitingForNearbyFacilities] = useState(!ssrData);
	const baseConfig = makeFacilitiesLoadingConfig(useAppContext(), brand);

	// The provider tree persists across client-side navigations (mounted once in _app), so when
	// getServerSideProps re-runs for a new facility (e.g. switching locations in the header),
	// resync local state from the freshly-provided ssrData instead of keeping the stale facility.
	useEffect(() => {
		if (ssrData?.facilityCode && ssrData.facilityCode !== facilityContextData?.facilityCode) {
			setFacilityContextData(ssrData);
			setIsWaitingForNearbyFacilities(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ssrData?.facilityCode]);

	useEffect(() => {
		// if shouldFetchNearbyFacility is false, we don't need to fetch the nearby facility
		if (!shouldFetchNearbyFacility) {
			return;
		}

		// tempFacilityCode starts empty (see useFacilityCode) to avoid hydration mismatches, then
		// resolves to its real value on a later tick. If we already have SSR-seeded office info,
		// skip fetching while tempFacilityCode is still the temporary empty placeholder so that
		// data isn't briefly clobbered by a spurious refetch on mount.
		if (!tempFacilityCode && facilityContextData?.officeInfo) {
			return;
		}

		// when we have a officeInfo and tempFacilityCode are the same then we don't need to fetch the facility again
		if (facilityContextData?.officeInfo && tempFacilityCode === facilityContextData?.officeInfo?.code) {
			// we do need to save the facility code to local/session storage
			setFacilityCode(tempFacilityCode);
			return;
		}
		const config: FacilitiesLoadingConfig = {
			...baseConfig,
			brand,
			defaultToCdn,
			user,
		};
		setIsWaitingForNearbyFacilities(true);
		void loadAllFacilitiesDataFromFacilityCode(tempFacilityCode, config).then((data) => {
			setIsWaitingForNearbyFacilities(false);
			if (!data?.officeInfo || !data?.facilityCode) {
				removeFacilityCode();
				return false;
			}
			setFacilityContextData(data);
			setFacilityCode(data.facilityCode || '');
			return true;
		});
	}, [
		baseConfig,
		tempFacilityCode,
		facilityContextData?.officeInfo,
		removeFacilityCode,
		setFacilityCode,
		defaultToCdn,
		user,
		brand,
		facilityCode,
		shouldFetchNearbyFacility,
	]);

	return {
		...(facilityContextData || makeDefaultFacilityContextData()),
		isWaitingForNearbyFacilities,
		facilityBrand: brandThemeToFacilityBrand(brand),
	};
}

/**
 * Configuration interface for facilities loading settings
 * @interface FacilitiesLoadingConfig
 * @property {string} appName - The name of the application
 * @property {string} appVersion - The version of the application
 * @property {string} bffGraphqlUrl - The URL for the BFF GraphQL endpoint
 * @property {TAGBrand} brand - The brand identifier
 * @property {boolean} [defaultToCdn] - Optional flag to default to CDN
 * @property {boolean} [useBeffeForFacility] - Optional flag to use BFFE for facility
 * @property {boolean} useFacilityBeffeDataSource - Flag to determine if facility BFFE data source should be used
 * @property {Maybe<IUser>} [user] - Optional user information
 */
export interface FacilitiesLoadingConfig {
	appName: string;
	appVersion: string;
	bffGraphqlUrl: string;
	brand: TAGBrand;
	defaultToCdn?: boolean;
	useBeffeForFacility?: boolean;
	useFacilityBeffeDataSource: boolean;
	user?: Maybe<IUser>;
}

export const loadAllFacilitiesDataFromFacilityCode = preventMultipleCalls(
	async function loadAllFacilitiesDataFromFacilityCode(
		facilityCode: string,
		config: FacilitiesLoadingConfig,
		coords?: { latitude: number; longitude: number } | null
	): Promise<SSRFacilityContextType> {
		let officeInfo: FacilityFieldsFragment | null = null;
		let positionCoords = coords || { latitude: 0, longitude: 0 };
		if (!coords || (coords?.latitude === 0 && coords?.longitude === 0)) {
			// part 1 get office info from facility code
			officeInfo = await getOfficeInfoFromFacilityCode(facilityCode, config);
			// part 2 get location from either the office or user
			positionCoords = await getPosition({
				defaultToCdn: config.defaultToCdn,
				user: config.user,
				officeInfo,
			});
		}
		// part 3 get nearby facilities
		const {
			facility,
			facilities = [],
			nearbyFacilities = [],
		} = await getNearbyFacilitiesFromLocation({ coords: positionCoords, config });
		officeInfo = facility || officeInfo;

		// part 4 get schedulingDetails scheduling
		const schedulingSystem = await getOnPageSchedulingDetails({
			coords: officeInfo?.location as IOnPageSchedulingDetails['coords'],
			brand: config.brand,
		});

		return {
			facilityCode: officeInfo?.code,
			officeInfo: officeInfo,
			locationState: {
				nearByOfficeLocations: facilities,
				closestOfficeInfo: facilities[0] ?? null,
				user: {
					hasIpBasedCoordinates: config.defaultToCdn ? config.user?.hasIpBasedCoordinates : false,
					coordinates: coords || { longitude: 0, latitude: 0 },
				},
				schedulingSystem: schedulingSystem,
			},
			nearbyFacilities: nearbyFacilities,
		};
	}
);

export async function getOfficeInfoFromFacilityCode(facilityCode: string, config: FacilitiesLoadingConfig) {
	if (!facilityCode) {
		return null;
	}
	try {
		// use BEFFE for facility data source
		const data = await getBffFacilityById(
			brandThemeToFacilityBrand(config.brand).toString() as BffBrandType,
			facilityCode,
			config.bffGraphqlUrl,
			config.appName,
			config.appVersion
		);
		const facilities = mapFacilities(data?.facilityById ? [data.facilityById] : null);
		return facilities?.[0]?.facility ?? null;
	} catch (error) {
		console.warn('Error fetching office info from facility code:', error);
		return null;
	}
}
